if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.hasAttribute('data-ughc-slideshow')) this.setupSlideshow();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }


      setupSlideshow() {
        this.previousControl = this.querySelector('[data-ughc-previous]');
        this.nextControl = this.querySelector('[data-ughc-next]');
        this.previousControl?.addEventListener('click', () => this.stepSlideshow(-1));
        this.nextControl?.addEventListener('click', () => this.stepSlideshow(1));
        this.elements.viewer.addEventListener('keydown', (event) => {
          if (event.target.closest('input,select,textarea,video,model-viewer')) return;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.stepSlideshow(event.key === 'ArrowRight' ? 1 : -1);
          }
        });
        this.elements.viewer.addEventListener('touchstart', (event) => {
          if (event.touches.length !== 1 || event.target.closest('video,model-viewer,deferred-media')) {
            this.touchStart = null;
            return;
          }
          this.touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        }, { passive: true });
        this.elements.viewer.addEventListener('touchmove', (event) => {
          if (event.touches.length !== 1) this.touchStart = null;
        }, { passive: true });
        this.elements.viewer.addEventListener('touchend', (event) => {
          if (!this.touchStart || event.changedTouches.length !== 1) return;
          const dx = event.changedTouches[0].clientX - this.touchStart.x;
          const dy = event.changedTouches[0].clientY - this.touchStart.y;
          this.touchStart = null;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
            this.suppressImageClickUntil = Date.now() + 400;
            this.stepSlideshow(dx < 0 ? 1 : -1);
          }
        }, { passive: true });
        this.elements.viewer.addEventListener('touchcancel', () => { this.touchStart = null; }, { passive: true });
        this.elements.viewer.addEventListener('click', (event) => {
          if (Date.now() < (this.suppressImageClickUntil || 0)) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, true);
        this.updateSlideshowControls();
      }

      slideshowItems() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]')).filter(item => !item.hidden);
      }

      stepSlideshow(direction) {
        const items = this.slideshowItems();
        if (items.length < 2) return;
        const current = Math.max(0, items.findIndex(item => item.classList.contains('is-active')));
        const next = (current + direction + items.length) % items.length;
        this.setActiveMedia(items[next].dataset.mediaId, false);
      }

      updateSlideshowControls() {
        if (!this.hasAttribute('data-ughc-slideshow')) return;
        const items = this.slideshowItems();
        const current = Math.max(0, items.findIndex(item => item.classList.contains('is-active')));
        const position = this.querySelector('[data-ughc-position]');
        const total = this.querySelector('[data-ughc-total]');
        if (position) position.textContent = String(current + 1);
        if (total) total.textContent = String(items.length);
        if (this.previousControl) this.previousControl.disabled = items.length < 2;
        if (this.nextControl) this.nextControl.disabled = items.length < 2;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-list > [data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-list > [data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.updateSlideshowControls();
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.hasAttribute('data-ughc-slideshow')) return;
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
